import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { OrganisationPermissionType } from '@assetaccountant/api/models/organisation-permission-type';
import { RegisterPermissionType } from '@assetaccountant/api/models/register-permission-type';
import { AssetsService } from '@assetaccountant/assets/services/assets.service';
import { AuthenticationService } from '@assetaccountant/core';
import { NavItem } from '@assetaccountant/core/models/nav-item';
import { createFyRange } from '@assetaccountant/core/resolvers/fy.resolver';
import { PeriodData, PeriodParams } from '@assetaccountant/core/resolvers/period.resolver';
import { CompareFn, SORT_COMPARE } from '@assetaccountant/core/services/compare.factory';
import { FeatureFlag, featureIsEnabled } from '@assetaccountant/core/services/feature-flag';
import { LayoutService } from '@assetaccountant/core/services/layout.service';
import { PoolsService } from '@assetaccountant/core/services/pools.service';
import {
  TypedActivatedRouteSnapshot,
  getChildRoute,
} from '@assetaccountant/core/utils/route-utils';
import { TypedNgbModal, setupModal } from '@assetaccountant/core/utils/typed-modal';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { filter } from 'rxjs/operators';

import { Feature } from '@assetaccountant/api/models/feature';
import { OrganisationDto } from '@assetaccountant/api/models/organisation.dto';
import { IzePipe } from '@assetaccountant/core/pipes/ize.pipe';
import { TrackingService } from '@assetaccountant/core/services/tracking.service';
import { environment } from '../../../../../../environments/environment';
import { RegimeApiModel } from '../../../models/regime-api-model';
import { Register } from '../../../models/register';
import { AppNavigationService } from '../../../services/app-navigation.service';
import { OrganisationService } from '../../../services/organisation.service';
import { Pool } from '../../pool-setting-selection/model/pool';
import { CreateRegisterModalComponent } from '../create-register-modal/create-register-modal.component';

@Component({
  selector: 'app-navigation-v2',
  templateUrl: './navigation-v2.component.html',
  styleUrls: ['./navigation-v2.component.scss'],
  standalone: false,
})
export class NavigationV2Component implements OnInit, OnDestroy {
  readonly FeatureFlag = FeatureFlag;
  readonly OrganisationPermissionType = OrganisationPermissionType;
  readonly organisationSettingsPermissions = [
    OrganisationPermissionType.UpdateOrganisation,
    OrganisationPermissionType.UpdateSubscription,
    OrganisationPermissionType.ListUsers,
  ];
  readonly RegisterPermissionType = RegisterPermissionType;
  readonly registerSettingsPermissions = [
    RegisterPermissionType.UpdateRegister,
    RegisterPermissionType.UpdateClassification,
    RegisterPermissionType.UpdateIntegrationConnection,
    RegisterPermissionType.UpdateUser,
  ];

  @Input() hideSearchbar = true;
  @Input() searchbarResultLink: (id, title) => string;

  private _minimal?: boolean;
  get minimal() {
    if (this._minimal == null) {
      this._minimal = !!localStorage.getItem('nav-minimal');
    }
    return this._minimal;
  }
  set minimal(value: boolean) {
    if (this._minimal !== value) {
      this._minimal = value;
      try {
        if (value) {
          localStorage.setItem('nav-minimal', 'true');
        } else {
          localStorage.removeItem('nav-minimal');
        }
      } catch {}
    }
  }

  currentOrganisation?: OrganisationDto;
  currentOrganisationLogo?: string;

  registers: Register[] = [];
  currentRegister?: Register;
  recentRegisters: Register[] = [];

  regimes: RegimeApiModel[] = [];
  currentRegime?: RegimeApiModel;

  currentPeriod?: { startDate: string; endDate: string; useFY: boolean };

  currentPools?: Pool[];

  currentView: NavItem | null;
  poolsExpanded = false;
  primaryRegisterNavigation: NavItem[] = [];
  primaryOrganisationNavigation: NavItem[] = [];
  settingsNavigation: NavItem[] = [];

  get demoRegister() {
    return this.registers?.find(r => r.isDemo);
  }

  get hasOnlyDemoRegister() {
    return this.currentRegister?.isDemo && this.registers?.length === 1;
  }

  get hasNoOrganisation() {
    return !this.currentOrganisation;
  }

  get isStagingApi() {
    return featureIsEnabled(FeatureFlag.UseStagingApi);
  }
  get isStagingUi() {
    return environment.isStagingUi;
  }

  get orgSettingsLink() {
    return (
      this.currentOrganisation && [
        '/organisations',
        this.currentOrganisation.organisationId,
        'settings',
        // eslint-disable-next-line ize/ize
        'organisation',
      ]
    );
  }

  get prodUiLink() {
    return environment.prodOpenIdConnect.redirectUri;
  }

  get userEmail() {
    return this.authService.getCurrentUserEmail();
  }

  get userName() {
    return this.authService.getCurrentUserName();
  }

  // TODO: Refactor when routing is redone
  private subscriptions = [
    this.router.events
      .pipe(filter((e: any): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        const { organisation, register, regime, period } = getChildRoute(
          this.router.routerState.snapshot.root as TypedActivatedRouteSnapshot<
            Partial<PeriodParams>,
            Partial<PeriodData>
          >,
        ).data;
        const periodWithFY = period && {
          ...period,
          useFY: /\/years\//.test(this.router.url),
        };

        const organisationChanged =
          this.currentOrganisation?.organisationId !==
          organisation?.activeOrganisation.organisationId;
        const registerChanged = this.currentRegister?.id !== register?.id;
        const regimeChanged = this.currentRegime?.id !== regime?.id;
        const periodChanged =
          this.currentPeriod?.startDate !== periodWithFY?.startDate ||
          this.currentPeriod?.endDate !== periodWithFY?.endDate ||
          this.currentPeriod?.useFY !== periodWithFY?.useFY;

        this.currentOrganisation = organisation?.activeOrganisation;
        this.currentOrganisationLogo = organisation?.activeOrganisationLogo;

        this.currentRegister = register;

        this.currentRegime = regime;

        this.currentPeriod = periodWithFY;

        this.refreshRegisterListing(organisationChanged);

        if (organisationChanged || registerChanged || regimeChanged || periodChanged) {
          // Pool membership doesn't depend on the period, only the FY segment of
          // the route does — so re-fetch pools only when org/register changed.
          this.refreshNavigation(organisationChanged || registerChanged);
        }

        this.getCurrentNavigationItem();
      }),

    this.layoutService.refreshNav.subscribe(() => {
      this.refreshNavigation();
      this.refreshRegisterListing(true);
    }),
  ];

  constructor(
    @Inject(SORT_COMPARE) private sortCompare: CompareFn,
    private appNavigationService: AppNavigationService,
    private authService: AuthenticationService,
    private organisationService: OrganisationService,
    private router: Router,
    private poolsService: PoolsService,
    public assetsService: AssetsService,
    private layoutService: LayoutService,
    @Inject(NgbModal) public ngbModal: TypedNgbModal,
    private izePipe: IzePipe,
    public trackingService: TrackingService,
  ) {}

  ngOnInit() {
    this.refreshNavigation();

    // TODO: move this permanently
    document.body.classList.add('overflow-visible');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());

    document.body.classList.remove('overflow-visible');
  }

  onUserChangedRegister(register: Register) {
    this.appNavigationService.switchRegister(register);
  }

  openCreateRegisterModal() {
    setupModal(this.ngbModal.open(CreateRegisterModalComponent));
  }

  registerDescription(register: Register, breakName: boolean = false) {
    if (register.name === register.entityName) {
      return register.name;
    }
    if (breakName) {
      return `${register.entityName} <br />  ${register.name}`;
    } else {
      return `${register.entityName} - ${register.name}`;
    }
  }

  refreshNavigation(updatePools: boolean = true) {
    const organisationSettingsNav = {
      id: 'org-settings',
      route: ['/organisations', this.currentOrganisation?.organisationId, 'settings'],
      name: this.izePipe.transform('Organisation Settings'),
      enabled: true,
      icon: 'settings',
      organisationPermissions: this.organisationSettingsPermissions,
    };

    if (this.currentRegister?.hasPermission) {
      this.primaryOrganisationNavigation = [];

      if (updatePools || !this.currentPools) {
        this.poolsService.getAllPools().subscribe(pools => {
          this.currentPools = pools;
          this.refreshPrimaryRegisterNavigation();
        });
      } else {
        this.refreshPrimaryRegisterNavigation();
      }

      this.settingsNavigation = [
        organisationSettingsNav,
        {
          id: 'settings',
          route: this.registerRoute('settings'),
          name: 'Register Settings',
          enabled: true,
          icon: 'settings',
          registerPermissions: this.registerSettingsPermissions,
        },
      ];
    } else {
      this.primaryRegisterNavigation = [];
      if (this.currentOrganisation) {
        this.settingsNavigation = [organisationSettingsNav];
        this.refreshPrimaryOrganisationNavigation();
      } else {
        this.primaryOrganisationNavigation = [];
      }
    }

    this.getCurrentNavigationItem();
  }

  organisationRoute(...params: any[]) {
    return ['/organisations', this.currentOrganisation?.organisationId || '-', ...params];
  }

  registerRoute(...params: any[]) {
    return this.organisationRoute('registers', this.currentRegister?.id || '-', ...params);
  }

  registerRegimeRoute(...params: any[]) {
    return this.registerRoute('regimes', this.currentRegime?.id || '-', ...params);
  }

  registerRegimePeriodRoute(...params: any[]) {
    return this.registerRegimeRoute('periods', '-', ...params);
  }
  registerRegimeYearRoute(...params: any[]) {
    return this.registerRegimeRoute(
      'years',
      createFyRange(
        this.currentPeriod?.startDate,
        this.currentPeriod?.startDate,
        this.currentRegister,
      ),
      ...params,
    );
  }
  registerRegimeYearRoutePool(pool, ...params: any[]) {
    return this.registerRegimeYearRoute('pools', pool.code, ...params);
  }

  getCurrentNavigationItem() {
    const navItems = this.primaryRegisterNavigation.concat(
      this.primaryOrganisationNavigation,
      this.settingsNavigation,
    );
    const flatNavItems = navItems.flatMap(item =>
      item.isExpandable && item.children ? item.children : [item],
    );
    this.currentView = this.appNavigationService.getCurrentNavigationItem(flatNavItems);
    this.appNavigationService.setSubNavigation(this.currentView?.children);

    // Auto-expand pools group when a pool is active
    const poolsGroup = navItems.find(item => item.isExpandable);
    if (poolsGroup?.children?.some(child => child.id === this.currentView?.id)) {
      this.poolsExpanded = true;
    }
  }

  togglePoolsExpanded() {
    this.poolsExpanded = !this.poolsExpanded;
  }

  private getPoolChildNavigation(pool: Pool): NavItem[] {
    const poolNav: NavItem[] = [
      {
        id: `pools-${pool.code}-reconciliation`,
        route: this.registerRegimeYearRoutePool(pool, `reconciliation`),
        name: 'Reconciliation',
        enabled: true,
      },
      {
        id: `pools-${pool.code}-transactions`,
        route: this.registerRegimeYearRoutePool(pool, `transactions`),
        name: 'Transactions',
        enabled: true,
      },
      {
        id: `pools-${pool.code}-opening-assets`,
        route: this.registerRegimeYearRoutePool(pool, `opening-assets`),
        name: 'Opening Assets',
        enabled: true,
      },
      {
        id: `pools-${pool.code}-additions`,
        route: this.registerRegimeYearRoutePool(pool, `additions`),
        name: 'Additions',
        enabled: true,
      },
      {
        id: `pools-${pool.code}-transfers`,
        route: this.registerRegimeYearRoutePool(pool, `transfers`),
        name: 'Transfers',
        enabled: true,
      },
      {
        id: `pools-${pool.code}-disposals`,
        route: this.registerRegimeYearRoutePool(pool, `disposals`),
        name: 'Disposals',
        enabled: true,
      },
      {
        id: `pools-${pool.code}-closing-assets`,
        route: this.registerRegimeYearRoutePool(pool, `closing-assets`),
        name: 'Closing Assets',
        enabled: true,
      },
    ];

    if (pool.recommendTransfers) {
      poolNav.push({
        id: `pools-${pool.code}-eligible-transfers`,
        route: this.registerRegimeYearRoutePool(pool, `eligible-transfers`),
        name: 'Eligible Transfers',
        enabled: true,
      });
    }

    return poolNav;
  }

  private getPoolNavigation(): NavItem[] {
    const poolIconMap: Record<string, string> = {
      CA_C1: 'pool-ca-c1',
      CA_C3: 'pool-ca-c3',
      CA_C6: 'pool-ca-c6',
      CA_C7: 'pool-ca-c7',
      CA_C8: 'pool-ca-c8',
      CA_C10: 'pool-ca-c10',
      CA_C12: 'pool-ca-c12',
      CA_C14_1: 'pool-ca-c14',
      CA_C43: 'pool-ca-c43',
      CA_C44: 'pool-ca-c44',
      CA_C46: 'pool-ca-c46',
      CA_C50: 'pool-ca-c50',
      CA_C53: 'pool-ca-c53',
    };

    const poolItems = (this.currentPools || [])
      .filter(p => p.enabled || p.hasAssets)
      .map(pool => ({
        id: `pools-${pool.code}`,
        route: this.registerRegimeYearRoute('pools', pool.code),
        name: pool.name.replace(/^(Class [\d.]+) - .+/, '$1'),
        enabled: true,
        icon: poolIconMap[pool.code] || 'circle',
        children: this.getPoolChildNavigation(pool),
      }));

    if (poolItems.length > 2) {
      return [
        {
          id: 'pools',
          route: poolItems[0].route,
          name: 'Pools',
          enabled: true,
          icon: 'circle',
          isExpandable: true,
          children: poolItems,
        },
      ];
    }

    return poolItems;
  }

  private refreshPrimaryOrganisationNavigation() {
    this.primaryOrganisationNavigation = [
      {
        id: 'reports',
        route: this.organisationRoute('reports'),
        name: 'Reports',
        enabled: true,
        icon: 'file-text',
        feature: Feature.ConsolidatedReports,
      },
    ];
    this.getCurrentNavigationItem();
  }

  private refreshPrimaryRegisterNavigation() {
    this.primaryRegisterNavigation = [
      {
        id: 'assets',
        route: this.registerRegimePeriodRoute('assets'),
        name: 'Assets',
        enabled: true,
        icon: 'box',
      },
      {
        id: 'add-assets',
        route: this.registerRegimePeriodRoute('assets', 'add'),
        name: 'Add Assets',
        enabled: true,
        icon: 'plus',
        registerPermissions: [RegisterPermissionType.CreateAsset],
      },

      ...this.getPoolNavigation(),
      {
        id: 'imports',
        route: this.registerRoute('imports'),
        name: 'Bulk Actions',
        enabled: true,
        icon: 'truck',
        registerPermissions: [RegisterPermissionType.CreateBulkAction],
      },
      {
        id: 'journals',
        route: this.registerRoute('journals'),
        name: 'Journals',
        enabled: true,
        icon: 'book',
      },
      {
        id: 'reports',
        route: this.registerRegimePeriodRoute('reports'),
        name: 'Reports',
        enabled: true,
        icon: 'file-text',
        registerPermissions: [RegisterPermissionType.CreateReport],
      },
    ];
    this.getCurrentNavigationItem();
  }

  // Unfortunately we need this right now to display the register listing which means we are getting a register
  // list in the resolver, this component and the view-settings component
  private refreshRegisterListing(shouldRefresh = true) {
    // Don't try to load registers if there's no current org
    if (shouldRefresh && this.currentOrganisation) {
      // It's OK that this isn't triggered on back - back button shouldn't trigger switchRegister
      this.organisationService.listRegisters().subscribe(({ registers }) => {
        this.appNavigationService.initializeRegisterData(registers);
        this.registers = registers;
        this.refreshRecentRegisters();
      });
    } else {
      this.refreshRecentRegisters();
    }
  }

  private refreshRecentRegisters() {
    this.recentRegisters = this.appNavigationService
      .getAllRegisters()
      .filter(
        r =>
          r.id !== this.appNavigationService.getActiveRegisterId() && !r.isDemo && r.hasPermission,
      )
      .sort((a, b) => {
        if (a.userLastAccessed || b.userLastAccessed) {
          if (!a.userLastAccessed) {
            return 1;
          }
          if (!b.userLastAccessed) {
            return -1;
          }
          return moment.utc(a.userLastAccessed).isSameOrAfter(b.userLastAccessed) ? -1 : 1;
        }
        return this.sortCompare(a.name, b.name);
      })
      .slice(0, 10);
  }
}
